import { default as default2 } from "debounce";
import { isFocusable } from "tabbable";
import { useCurrentBreakPoint } from "./hooks/use-current-breakpoint.mjs";
import { useIsMounted } from "./hooks/use-is-mounted.mjs";
import { useResponsiveProps } from "./hooks/use-responsive-props.mjs";
import { usePrevious } from "./hooks/use-previous.mjs";
let scrollAnimationFrame = 0;
const isScrollDisabled = () => {
  return document.body.classList.contains("is-scroll-disabled");
};
const getHashArray = () => {
  const hash = window.location.hash;
  if (hash) {
    return hash.split("/").filter((item) => {
      return !item.startsWith("?") && item.length !== 1 && item.length !== 0;
    }).map((item) => {
      if (item.startsWith("#")) {
        return item.substring(1);
      } else {
        return item;
      }
    });
  } else {
    return false;
  }
};
const swingTo = (pos) => {
  pos /= 0.5;
  if (pos < 1) {
    return 0.5 * Math.pow(pos, 2);
  }
  pos -= 2;
  return -0.5 * (pos * pos - 2);
};
const doScrolling = (scrollTargetY, speed, parent = window, callback) => {
  window.cancelAnimationFrame(scrollAnimationFrame);
  const scrollY = parent instanceof HTMLElement ? parent.scrollTop : parent.scrollY;
  let currentTime = 0;
  const time = Math.max(0.1, Math.min(Math.abs(scrollY - scrollTargetY) / speed, 0.8));
  function tick() {
    currentTime += 1 / 60;
    const p = currentTime / time;
    const t = swingTo(p);
    if (p < 1) {
      scrollAnimationFrame = window.requestAnimationFrame(tick);
      parent.scrollTo(0, scrollY + (scrollTargetY - scrollY) * t);
    } else {
      parent.scrollTo(0, scrollTargetY);
      if (callback) {
        callback();
      }
    }
  }
  tick();
};
const getScrollableContainer = (element) => {
  const backdrop = element.closest(".modal");
  return backdrop instanceof HTMLElement ? backdrop : window;
};
const scrollToElement = (element, speed, offset2, focusScrolledElement) => {
  if (typeof element === "string") {
    element = document.getElementById(element);
  }
  if (element) {
    const duration = speed != null ? speed : 500;
    const offsetY = element.getBoundingClientRect().top + window.scrollY;
    const topOffsetY = offset2 != null ? offset2 : getDefaultPageTopOffset(offsetY);
    const combinedOffsetY = Math.max(offsetY - topOffsetY, 0);
    const callback = focusScrolledElement ? () => {
      if (element instanceof HTMLElement) {
        if (!isFocusable(element)) {
          element.setAttribute("tabindex", "-1");
        }
        element.focus();
      }
    } : void 0;
    doScrolling(combinedOffsetY, duration, getScrollableContainer(element), callback);
  }
};
const isOnScreen = (element) => {
  if (typeof element === "string") {
    element = document.getElementById(element);
  }
  if (element) {
    const rect = element.getBoundingClientRect();
    const html = document.documentElement;
    if (html) {
      return rect.top >= getDefaultPageTopOffset() && rect.left >= 0 && rect.bottom <= (window.innerHeight || html.clientHeight) && rect.right <= (window.innerWidth || html.clientWidth);
    }
  }
  return false;
};
const isHeaderOpen = (refOffsetY) => {
  const header = document.getElementById("header");
  if (!refOffsetY) {
    return !!header && !header.classList.contains("has-scrolled");
  } else {
    const documentHeight = document.documentElement ? document.documentElement.offsetHeight : 0;
    const windowHeight = window.innerHeight;
    const headerHeight = header ? header.clientHeight : 0;
    if (refOffsetY <= headerHeight || windowHeight + refOffsetY >= documentHeight - headerHeight) {
      return true;
    }
    return false;
  }
};
const getDefaultPageTopOffset = (refOffsetY) => {
  const affixes = document.getElementsByClassName("affix");
  let pageTopOffset = 0;
  for (const affix of affixes) {
    pageTopOffset += affix.clientHeight;
  }
  return isHeaderOpen(refOffsetY) ? pageTopOffset + 150 : pageTopOffset;
};
const stringToNum = (str) => {
  return Number(str.replace(",", "."));
};
const numToString = (num, decimals) => {
  return num.toFixed(decimals).toString().replace(".", ",");
};
const offset = (element) => {
  var _a, _b;
  const rect = element.getBoundingClientRect();
  const scrollLeft = window.scrollX || ((_a = document.documentElement) == null ? void 0 : _a.scrollLeft);
  const scrollTop = window.scrollX || ((_b = document.documentElement) == null ? void 0 : _b.scrollTop);
  return {
    left: rect.left + (scrollLeft || 0),
    top: rect.top + (scrollTop || 0)
  };
};
const setDocumentScrollTop = (scrollPosition) => {
  if (document.documentElement) {
    document.documentElement.scrollTop = document.body.scrollTop = scrollPosition;
  }
};
const getParameterByName = (name, url = window.location.href) => {
  const regex = new RegExp("[?&]" + name.replace(/[[\]]/g, "\\$&") + "(=([^&#]*)|&|#|$)");
  const results = regex.exec(url);
  if (!results) {
    return null;
  }
  if (!results[2]) {
    return "";
  }
  return decodeURIComponent(results[2].replace(/\+/g, " "));
};
const getParentsUntil = (elem, parent) => {
  const parents = [];
  for (; elem && elem !== document.documentElement; elem = elem.parentNode) {
    if (parent && elem === parent) {
      break;
    }
    parents.push(elem);
  }
  return parents;
};
const getOffsetFromParent = (element, parent) => {
  const pos = {
    left: 0,
    top: 0
  };
  getParentsUntil(element, parent).forEach((parentEl) => {
    if (parentEl instanceof HTMLElement) {
      const parentElStyle = getComputedStyle(parentEl);
      if (parentElStyle.position === "static") {
        return;
      }
      pos.top += parentEl.offsetTop;
      pos.left += parentEl.offsetLeft;
    }
  });
  return pos;
};
export {
  default2 as debounce,
  doScrolling,
  getDefaultPageTopOffset,
  getHashArray,
  getOffsetFromParent,
  getParameterByName,
  getParentsUntil,
  isHeaderOpen,
  isOnScreen,
  isScrollDisabled,
  numToString,
  offset,
  scrollToElement,
  setDocumentScrollTop,
  stringToNum,
  swingTo,
  useCurrentBreakPoint,
  useIsMounted,
  usePrevious,
  useResponsiveProps
};
